import { ReactNode, useState } from 'react';

import { Checkbox, Dialog, LinkAnchor } from '@hcs/design-system';
import { useTrackEngagementEvent } from '@hcs/engagement-tracking';
import { useNavigationContext } from '@hcs/navigation';
import { useOrgSelfServeInfo } from '@hcs/self-serve-info';
import {
  CapabilityApplications,
  CapabilityKeys,
  MeaningfulEventTypes,
} from '@hcs/types';
import { formatNumber } from '@hcs/utils';

import {
  getLocalStorageDontShowAgain,
  setLocalStorageDontShowAgain,
} from '../../utils/orgPricing.utils';

import styles from './OrgPricingConfirmDialog.module.css';

export interface OrgPricingConfirmDialogProdcutProps {
  active: boolean;
  capabilityKey: CapabilityKeys;
  onAccept: VoidFunction;
  onClose: VoidFunction;
}
interface OrgPricingConfirmDialogProps {
  active: boolean;
  applicationKey: CapabilityApplications;
  capabilityKey: CapabilityKeys;
  includedInTier: number | null;
  onAccept: VoidFunction;
  onClose: VoidFunction;
  children: ReactNode;
  unitDescriptorPlural: string;
  actionDescriptor: string;
  disclaimer?: string;
}

export const OrgPricingConfirmDialog = ({
  applicationKey,
  active,
  onClose,
  onAccept,
  includedInTier,
  children,
  actionDescriptor,
  unitDescriptorPlural,
  disclaimer,
}: OrgPricingConfirmDialogProps) => {
  const { billingDashboardLink, usageReportLink } = useNavigationContext();
  const dontShowAgainLS = getLocalStorageDontShowAgain(applicationKey);
  const [dontShowAgain, setDontShowAgain] = useState(
    dontShowAgainLS === null || dontShowAgainLS === 'true',
  );
  const { data: orgSsInfo } = useOrgSelfServeInfo();
  const { mutate: trackEngagementEvent } = useTrackEngagementEvent();
  const dataHcName = `org-pricing-confirm-dialog-${applicationKey}`;
  return (
    <Dialog
      dataHcName={dataHcName}
      title="Pricing Confirmation"
      active={active}
      onClose={onClose}
      isOverlayDialog
      actions={[
        {
          dataHcName: `${dataHcName}-cancel`,
          label: 'Cancel',
          onClick: onClose,
          secondary: true,
        },
        {
          dataHcName: `${dataHcName}-accept`,
          label: 'I Understand',
          onClick: () => {
            setLocalStorageDontShowAgain(applicationKey, dontShowAgain);
            trackEngagementEvent({
              event_name: 'pricing-confirmation-accept',
              event_type: MeaningfulEventTypes.Goal,
              event_data: {
                application_key: applicationKey,
              },
            });
            onAccept();
          },
          primary: true,
        },
      ]}
    >
      <div>
        {children}
        <p>
          By clicking “I Understand”, you acknowledge that you will be billed at
          the end of your current billing period for any {unitDescriptorPlural}{' '}
          {actionDescriptor}
          {includedInTier ? (
            <>
              {' '}
              in excess of the {formatNumber(includedInTier)} included as part
              of your {orgSsInfo?.self_serve_plan?.name} plan
            </>
          ) : (
            ''
          )}
          . {disclaimer ? <>{disclaimer} </> : null}You can view your current
          usage at any time through your{' '}
          <LinkAnchor
            dataHcName="billing-dashboard-link"
            to={billingDashboardLink || ''}
            target="_blank"
            rel="noreferrer"
          >
            Billing Dashboard
          </LinkAnchor>{' '}
          or by creating a{' '}
          <LinkAnchor
            dataHcName="usage-report-link"
            to={usageReportLink || ''}
            target="_blank"
            rel="noreferrer"
          >
            Usage Report
          </LinkAnchor>
          .
        </p>
        <p className={styles.DontShowAgain}>
          <Checkbox
            dataHcName={`${dataHcName}-checkbox-dont-show-again-${applicationKey}`}
            checked={dontShowAgain}
            onChange={(v) => {
              if (v) {
                setDontShowAgain(true);
              } else {
                setDontShowAgain(false);
              }
            }}
          >
            Do not show this message again
          </Checkbox>
        </p>
      </div>
    </Dialog>
  );
};
